import { useState, useEffect, useRef } from 'react';
import Eyebrow from '../ui/Eyebrow.jsx';
import { howWeWorkHead, howWeWorkFoot, principles } from '../../content/howWeWork.js';

const N = principles.length; // 6

/**
 * HowWeWork — six operating principles rendered as an animated
 * horizontal timeline with a drawing spine, traveling pulse,
 * and sequentially-activated nodes.
 *
 * Layout: PrinciplesV2 (Horizontal Timeline) from design handoff.
 */
export default function HowWeWork() {
  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const timelineRef = useRef(null);

  // -1 means no node is active yet (before the loop kicks in)
  const [activeIdx, setActiveIdx] = useState(-1);

  // Per-node "has entered view" flags. Tracked in state (not via classList)
  // so each setActiveIdx re-render rebuilds the node className without
  // stripping the imperatively-added is-in class.
  const [nodesInView, setNodesInView] = useState(() => Array(N).fill(false));

  // Fire the is-in class on head and timeline when the section enters view.
  // We also need a boolean to gate the JS activation loop, so we track it
  // in a ref to avoid stale-closure issues inside the recursive setTimeout.
  const inViewRef = useRef(false);

  useEffect(() => {
    const section = sectionRef.current;
    const head = headRef.current;
    const timeline = timelineRef.current;
    if (!section || !head || !timeline) return;

    // SSR / old-browser fallback
    if (typeof IntersectionObserver === 'undefined') {
      head.classList.add('is-in');
      timeline.classList.add('is-in');
      inViewRef.current = true;
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !inViewRef.current) {
            head.classList.add('is-in');
            timeline.classList.add('is-in');
            inViewRef.current = true;
            io.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  // Node activation loop — starts 1600ms after the section enters view
  // so the spine-draw animation can finish first.
  // Guard: skip entirely when prefers-reduced-motion is set.
  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    let timeout;
    let stepIndex = 0;

    const waitForInView = () => {
      if (!inViewRef.current) {
        timeout = setTimeout(waitForInView, 100);
        return;
      }
      // Spine draw finishes around 1.6s after is-in fires; start first step then.
      timeout = setTimeout(step, 1600);
    };

    const step = () => {
      setActiveIdx(stepIndex % N);
      stepIndex++;
      // Extra 900ms pause when wrapping back to 0 (total 2200ms at boundary)
      const delay = stepIndex % N === 0 ? 2200 : 1300;
      timeout = setTimeout(step, delay);
    };

    waitForInView();
    return () => clearTimeout(timeout);
  }, []);

  // Mark each node "in view" the first time it crosses the viewport.
  // Tracked via state (not classList) so React re-renders driven by
  // setActiveIdx don't strip the is-in class from the JSX className.
  // CSS staggered transition-delay handles the staggered entry feel.
  useEffect(() => {
    const nodes = sectionRef.current?.querySelectorAll('.pv2-node');
    if (!nodes?.length) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNodesInView(Array(N).fill(true));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.nodeIdx);
            if (!Number.isNaN(idx)) {
              setNodesInView((prev) => {
                if (prev[idx]) return prev;
                const next = prev.slice();
                next[idx] = true;
                return next;
              });
            }
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <section id="how-we-work" className="pv2-root" ref={sectionRef}>
      <div className="container-page">

        <div className="pv2-head" ref={headRef}>
          <Eyebrow>{howWeWorkHead.eyebrow}</Eyebrow>
          <h2 className="pv2-heading">
            Principles,{' '}
            <span className="af-gradient-accent">not pitch decks.</span>
          </h2>
          <p className="pv2-desc">{howWeWorkHead.description}</p>
        </div>

        <div className="pv2-timeline" ref={timelineRef}>
          {/* Spine renders inside the grid (row 2) so it sits exactly on the
              up/down row boundary. It must come AFTER the mapped nodes so the
              :nth-child(1..6) column placement in CSS targets the nodes. */}
          <div className="pv2-nodes">
            {principles.map((p, i) => {
              const above = i % 2 === 0;
              const isActive = i === activeIdx;
              return (
                <div
                  key={p.number}
                  data-node-idx={i}
                  className={`pv2-node ${above ? 'pv2-node--up' : 'pv2-node--down'} ${nodesInView[i] ? 'is-in' : ''} ${isActive ? 'is-active' : ''}`}
                  style={{ transitionDelay: `${900 + i * 140}ms` }}
                >
                  <div className="pv2-card">
                    <div className="pv2-card-head">
                      <span className="pv2-num">{p.number}</span>
                      <h3 className="pv2-title">{p.title}</h3>
                    </div>
                    <p className="pv2-body">{p.body}</p>
                  </div>
                  <span className="pv2-connector" />
                  <span className="pv2-dot">
                    <span className="pv2-dot-ring" />
                    <span className="pv2-dot-core" />
                  </span>
                </div>
              );
            })}
            <div className="pv2-spine" aria-hidden="true">
              <div className="pv2-spine-base" />
              <div className="pv2-spine-fill" />
              <div className="pv2-pulse" />
            </div>
          </div>
        </div>

        <p className="text-body text-ink-200 text-center mt-14">
          {howWeWorkFoot.line}
        </p>

      </div>
    </section>
  );
}
